import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { PublicClientApplication, EventType, AuthenticationResult } from '@azure/msal-browser';
import { msalConfig, msalConfigCorporate, getAuthProvider, setAuthProvider } from './config';
import { consumeStoredImpersonationEmail, getImpersonationUrl } from './utils/impersonation';

// Initialize Application Insights telemetry as early as possible
import './telemetry/appInsights';

import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/index.css';

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * We create two instances: one for B2C (external candidates) and one for corporate Azure AD (Microsoft employees).
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalB2CInstance = new PublicClientApplication(msalConfig);
const msalCorporateInstance = new PublicClientApplication(msalConfigCorporate);

// Setup event callbacks for B2C instance
msalB2CInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    if (payload.account) {
      msalB2CInstance.setActiveAccount(payload.account);
    }
  }
});

// Setup event callbacks for Corporate instance
msalCorporateInstance.addEventCallback((event) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const payload = event.payload as AuthenticationResult;
    if (payload.account) {
      msalCorporateInstance.setActiveAccount(payload.account);
    }
  }
});

// Determine which provider based on the current URL path
const getProviderFromPath = (): 'b2c' | 'corporate' | null => {
  const path = window.location.pathname;
  if (path.startsWith('/auth/b2c')) {
    return 'b2c';
  } else if (path.startsWith('/auth/corporate')) {
    return 'corporate';
  }
  return null;
};

// Initialize both MSAL instances, then handle redirect only on the correct instance
Promise.all([msalB2CInstance.initialize(), msalCorporateInstance.initialize()])
  .then(async () => {
    // Determine which instance should handle the redirect based on URL path
    const pathProvider = getProviderFromPath();
    const storedProvider = getAuthProvider();

    // URL path takes precedence - this is where Azure redirects back to
    const activeProvider = pathProvider || storedProvider;

    console.log(
      `[MSAL Init] Path: ${window.location.pathname}, pathProvider: ${pathProvider}, storedProvider: ${storedProvider}, activeProvider: ${activeProvider}`
    );

    if (activeProvider === 'corporate') {
      console.log('[MSAL Init] Handling corporate redirect...');
      const response = await msalCorporateInstance.handleRedirectPromise();
      if (response) {
        console.log('[MSAL Init] Corporate login successful');
        setAuthProvider('corporate');
        // Redirect to home after successful login
        if (pathProvider) {
          const impersonationEmail = consumeStoredImpersonationEmail();
          const destination = impersonationEmail ? getImpersonationUrl(impersonationEmail) : '/';
          window.history.replaceState({}, document.title, destination);
        }
      }
    } else if (activeProvider === 'b2c') {
      console.log('[MSAL Init] Handling B2C redirect...');
      const response = await msalB2CInstance.handleRedirectPromise();
      if (response) {
        console.log('[MSAL Init] B2C login successful');
        setAuthProvider('b2c');
        // Redirect to home after successful login
        if (pathProvider) {
          window.history.replaceState({}, document.title, '/');
        }
      }
    } else {
      // No provider determined - handle both but expect one to fail
      await Promise.all([
        msalB2CInstance.handleRedirectPromise().catch(() => null),
        msalCorporateInstance.handleRedirectPromise().catch(() => null),
      ]);
    }

    // Default to using the first account if no account is active on page load
    if (!msalB2CInstance.getActiveAccount() && msalB2CInstance.getAllAccounts().length > 0) {
      msalB2CInstance.setActiveAccount(msalB2CInstance.getAllAccounts()[0]);
    }
    if (!msalCorporateInstance.getActiveAccount() && msalCorporateInstance.getAllAccounts().length > 0) {
      msalCorporateInstance.setActiveAccount(msalCorporateInstance.getAllAccounts()[0]);
    }

    const container = document.getElementById('root');
    if (container) {
      const root = createRoot(container);
      root.render(<App b2cInstance={msalB2CInstance} corporateInstance={msalCorporateInstance} />);
    }
  })
  .catch((error) => {
    console.error('MSAL initialization error:', error);
    // Import dynamically to avoid circular dependency at module load time
    import('./telemetry/appInsights').then(({ appInsights }) => {
      appInsights.trackException({
        exception: error instanceof Error ? error : new Error(String(error)),
        properties: { component: 'index', action: 'msalInitialization' },
      });
    });
  });
